import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { useAvailableCommoditiesForSwitchQuery } from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import "@/styles/SimpleKeyboardOverrides.css";
import {
  HelmetDexteel,
  TimeAndUserMenu,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import Add from "@mui/icons-material/Add";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Block from "@mui/icons-material/Block";
import Close from "@mui/icons-material/Close";
import Comment from "@mui/icons-material/Comment";
import Edit from "@mui/icons-material/Edit";
import KeyboardReturn from "@mui/icons-material/KeyboardReturn";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Replay from "@mui/icons-material/Replay";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useBucketsQuery } from "@/pages/sy/hooks/queries/useBucketsQuery";
import { useChargeLayersQuery } from "@/pages/sy/hooks/queries/useChargeLayersQuery";
import {
  useOpenChargeMutation,
  useUpsertChargeMutation,
} from "@/pages/sy/hooks/queries/useChargeMutations";
import { useChargesQuery } from "@/pages/sy/hooks/queries/useChargesQuery";
import { useCommoditiesQuery } from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import { useOperatorsQuery } from "@/pages/sy/hooks/queries/useOperatorsQuery";
import { useRealtimeInvalidation } from "@/pages/sy/hooks/queries/useRealtimeInvalidation";
import { useRecipeLayersQuery } from "@/pages/sy/hooks/queries/useRecipeLayersQuery";
import { useRecipesQuery } from "@/pages/sy/hooks/queries/useRecipesQuery";
import { useChargeColorsAndWeights } from "@/pages/sy/hooks/useColorsAndWeight";
import { Charge, ChargeStatusCode } from "@/pages/sy/models/Charge";
import { ChargeLayer } from "@/pages/sy/models/ChargeLayer";

import { chargeOperationStyles } from "@/pages/sy/styles/ChargeOperationStyles";
import { CHARGE_NUMBERS } from "@/utils/constants";
import { NewChargePanel } from "./components/NewChargePanel";
import { TableCurrentLayers } from "./components/TableCurrentLayers";
import { TableRecipeLayers } from "./components/TableRecipeLayers";
import { useBucketHandlers } from "./hooks/useBucketHandlers";
import { useChargeHandlers } from "./hooks/useChargeHandlers";
import { useCommentsHandlers } from "./hooks/useCommentsHandlers";
import { useCustomHandlers } from "./hooks/useCustomHandlers";
import { useHeatHandlers } from "./hooks/useHeatHandlers";
import { useLayerHandlers } from "./hooks/useLayerHandlers";
import { useRecipeHandlers } from "./hooks/useRecipeHandlers";
import { useRequestedWeightLayerSuggestions } from "./hooks/useRequestedWeightLayerSuggestions";
import { BucketModal } from "./modals/BucketModal";
import { ChargeLayerModalForOperation } from "./modals/ChargeLayerModalForOperation";
import { CommentsModal } from "./modals/CommentsModal";
import { CommodityModal } from "./modals/CommodityModal";
import { ConfirmRecipeModal } from "./modals/ConfirmRecipeModal";
import { ErrorDetail, ErrorDetailsModal } from "./modals/ErrorDetailsModal";
import { HeatNameModal } from "./modals/HeatNameModal";
import { RecipeModal } from "./modals/RecipeModal";
import { RequestedHeatWeightModal } from "./modals/RequestedHeatWeightModal";
import { WarningModal } from "./modals/WarningModal";
import { formatCraneMagnitude } from "./utils/formatCraneMagnitude";
import { calculateHeatWeightScaling } from "./utils/heatWeightScaling";
import {
  alignChargeAndRecipeLayers,
  type LayerAlignmentRow,
} from "./utils/layerAlignment";

const STATUS_NAMES = {
  S: "Scheduled",
  O: "Open",
  C: "Closed",
  H: "EAF",
} as const;

const getStatusName = (code: ChargeStatusCode | ""): string =>
  code ? STATUS_NAMES[code] : "N/A";

const getScheduledHeatName = (charge: Charge | null) => {
  if (!charge) return "N/A";
  if (!charge.ScheduledHeatName) return "N/A";
  return charge.ScheduledHeatName;
};

const getSelectedRecipeName = (charge: Charge | null) => {
  if (!charge?.RecipeName) return "N/A";
  if (charge.ChargeNumber != null) {
    return `${charge.RecipeName} #${charge.ChargeNumber}`;
  }
  return charge.RecipeName;
};

const isRecipeEnabled = (charge: Charge | null) => {
  if (!charge?.RecipeId) return true; // No recipe assigned, show as normal
  return charge.RecipeIsEnable ?? true;
};

const MAX_CHARGES = 6;
const ENABLE_VISUAL_LAYER_ALIGNMENT = false;

export const ChargeOperationScreen = () => {
  const theme = useTheme();
  const styles = useMemo(() => chargeOperationStyles(theme), [theme]);

  const location = useLocation();
  const navigate = useNavigate();
  const { chargeId } = useParams<{
    chargeId: string;
    action: string;
    chargeGroupNumber?: string;
  }>();
  const queryClient = useQueryClient();

  // === TanStack Query Mutations ===
  const upsertChargeMutation = useUpsertChargeMutation();
  const openChargeMutation = useOpenChargeMutation();
  const { state: utlState } = useUTLSettingsContext();
  const refreshTime = parseInt(
    get(utlState.settings, "SCRAP_CRANE_APP_REFRESH_TIME", 1),
  );

  const onHoldTime = parseInt(
    get(utlState.settings, "SCRAP_CRANE_APP_ON_HOLD_TIME", 1),
  );

  // === TanStack Query: Charges data ===
  const { data: allCharges = [], isLoading: isChargesLoading } =
    useChargesQuery(refreshTime);
  useRealtimeInvalidation();

  const charge = useMemo(
    () =>
      chargeId
        ? (allCharges.find((c) => c.ChargeId === parseInt(chargeId)) ?? null)
        : null,
    [allCharges, chargeId],
  );

  const charges = useMemo(
    () =>
      charge?.ScheduledHeatName
        ? allCharges.filter(
            (c) => c.ScheduledHeatName === charge.ScheduledHeatName,
          )
        : [],
    [allCharges, charge?.ScheduledHeatName],
  );

  const currentHeatName = allCharges[0]?.CurrentHeatName ?? null;

  // Refetch charges and return fresh data — used by handlers that need the data
  // (e.g. useChargeHandlers needs to find a newly created charge to navigate to it)
  const refetchCharges = useCallback(async (): Promise<Charge[]> => {
    await queryClient.refetchQueries({ queryKey: ["charges"] });
    return queryClient.getQueryData<Charge[]>(["charges"]) ?? [];
  }, [queryClient]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState<ChargeLayer | null>(null);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogData, setErrorDialogData] = useState<{
    title: string;
    message: string;
    details?: ErrorDetail[];
    detailsTitle?: string;
    guidanceMessage?: string;
    isBucketConflict?: boolean;
  } | null>(null);

  const setError = useCallback((message: string) => {
    if (message) {
      setErrorDialogData({
        title: "Error",
        message: message,
      });
      setErrorDialogOpen(true);
    }
  }, []);

  // Recipe layers from TanStack Query - automatically refetches when charge.RecipeId changes
  const { data: recipeLayersData = [] } = useRecipeLayersQuery(
    charge?.RecipeId,
    charge?.ChargeNumber,
  );

  // Sort layers and calculate target weight
  const recipeLayers = useMemo(
    () =>
      [...recipeLayersData].sort(
        (a, b) => (b.LayerNumber ?? 0) - (a.LayerNumber ?? 0),
      ),
    [recipeLayersData],
  );

  const recipeTargetWeight = useMemo(
    () => recipeLayers.reduce((sum, layer) => sum + (layer.Weight || 0), 0),
    [recipeLayers],
  );

  const heatWeightScaling = useMemo(
    () => calculateHeatWeightScaling(charges),
    [charges],
  );

  const scaledChargeTarget = useMemo(
    () =>
      charge?.ChargeId == null
        ? null
        : (heatWeightScaling?.targetsByChargeId.get(charge.ChargeId) ?? null),
    [charge?.ChargeId, heatWeightScaling],
  );

  const hasScaledTarget = scaledChargeTarget != null;

  // selectedRecipeId is used for the recipe selection modal
  const [selectedRecipeId, setSelectedRecipeId] = useState<number | null>(null);
  const [isRecipeDialogOpen, setIsRecipeDialogOpen] = useState(false);
  const [showRecipeConfirmModal, setShowRecipeConfirmModal] = useState(false);
  const [isBucketDialogOpen, setIsBucketDialogOpen] = useState(false);
  const [isLayerModalOpen, setIsLayerModalOpen] = useState(false);
  const [isEditingManualWeight, setIsEditingManualWeight] = useState(false);
  const [currentOperatorId, setCurrentOperatorId] = useState<number | null>(
    null,
  );
  const [currentChargeSequenceNumber, setCurrentChargeSequenceNumber] =
    useState<number | null>(null);
  const [currentChargeNumber, setCurrentChargeNumber] = useState<number | null>(
    null,
  );
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [isHeatNameDialogOpen, setIsHeatNameDialogOpen] = useState(false);
  const [heatNameInput, setHeatNameInput] = useState("");

  const [heatConflict, setHeatConflict] = useState<{
    isOpen: boolean;
    heatName: string;
  }>({ isOpen: false, heatName: "" });
  const [conflictingCharge, setConflictingCharge] = useState<Charge | null>(
    null,
  );
  const [commentInput, setCommentInput] = useState("");
  const chargeNumbers = useMemo(() => {
    if (charge?.ChargeGroupNumber) {
      const numbers = charges
        .filter((c: Charge) => c.ChargeGroupNumber === charge.ChargeGroupNumber)
        .map((c: Charge) => c.ChargeNumber)
        .filter((n): n is number => n !== null && n !== undefined)
        .sort((a, b) => a - b);
      if (numbers.length > 0) return numbers;
    }
    return [...CHARGE_NUMBERS];
  }, [charge?.ChargeGroupNumber, charges]);
  const [isNewChargeMode, setIsNewChargeMode] = useState(false);
  const [isChangingCharge, setIsChangingCharge] = useState(false);
  const [isEAFConfirmModalOpen, setIsEAFConfirmModalOpen] = useState(false);
  const [commoditySwitchState, setCommoditySwitchState] = useState<{
    isOpen: boolean;
    originalCommodityId: number | null;
    selectedCommodityId: number | null;
    originalRecipeCommodity: {
      commodityId: number;
      commodityName: string;
      commodityColor: string | null;
    } | null;
  }>({
    isOpen: false,
    originalCommodityId: null,
    selectedCommodityId: null,
    originalRecipeCommodity: null,
  });

  // Fetch available commodities for the fast-switch modal (substitutes or all if none configured)
  const {
    data: { commodities: availableCommoditiesForSwitch, hasSubstitutes } = {
      commodities: [],
      hasSubstitutes: false,
    },
  } = useAvailableCommoditiesForSwitchQuery(
    commoditySwitchState.originalCommodityId,
  );

  const { data: buckets = [] } = useBucketsQuery();
  const { data: operators = [] } = useOperatorsQuery();
  const { data: recipes = [] } = useRecipesQuery(true, true);
  const { data: commodities = [] } = useCommoditiesQuery();
  // Helper to invalidate static data queries (replaces old refreshData from context)
  const refreshData = useCallback(
    async (options?: { buckets?: boolean; recipes?: boolean }) => {
      const promises: Promise<void>[] = [];
      if (options?.buckets) {
        promises.push(queryClient.invalidateQueries({ queryKey: ["buckets"] }));
      }
      if (options?.recipes) {
        promises.push(queryClient.invalidateQueries({ queryKey: ["recipes"] }));
      }
      await Promise.all(promises);
    },
    [queryClient],
  );

  const selectedOperator = operators.find(
    (op) => op.OperatorId === currentOperatorId,
  );

  // === TanStack Query: Charge Layers data ===
  const { data: rawChargeLayers = [] } = useChargeLayersQuery(
    charge?.ChargeId,
    refreshTime,
  );

  const chargeLayers = useMemo(
    () => [...rawChargeLayers].sort((a, b) => b.LayerNumber - a.LayerNumber),
    [rawChargeLayers],
  );

  const { currentLayerRows, recipeLayerRows } = useMemo<{
    currentLayerRows: LayerAlignmentRow[];
    recipeLayerRows: LayerAlignmentRow[];
  }>(() => {
    if (ENABLE_VISUAL_LAYER_ALIGNMENT) {
      const alignedRows = alignChargeAndRecipeLayers(
        chargeLayers,
        recipeLayers,
      );
      return {
        currentLayerRows: alignedRows,
        recipeLayerRows: alignedRows,
      };
    }

    return {
      currentLayerRows: chargeLayers.map((chargeLayer) => ({
        chargeLayer,
        recipeLayer: null,
      })),
      recipeLayerRows: recipeLayers.map((recipeLayer) => ({
        chargeLayer: null,
        recipeLayer,
      })),
    };
  }, [chargeLayers, recipeLayers]);

  const totalWeight = useMemo(
    () => rawChargeLayers.reduce((sum, layer) => sum + (layer.Weight || 0), 0),
    [rawChargeLayers],
  );

  const requestedWeightLayerSuggestions = useRequestedWeightLayerSuggestions({
    recipeLayers,
    chargeLayers: rawChargeLayers,
    scaledTargetWeight: scaledChargeTarget?.scaledTotalTargetWeight,
  });

  const scrapWeight = useMemo(
    () => totalWeight - (charge?.FluxWeight ?? 0),
    [totalWeight, charge?.FluxWeight],
  );

  const maxLayerNumber = useMemo(
    () =>
      chargeLayers.length > 0
        ? Math.max(...chargeLayers.map((layer) => layer.LayerNumber))
        : 0,
    [chargeLayers],
  );

  const {
    setBucketCapacity,
    setUiCapacity,
    getCommodityColor,
    getBgColorWithAlpha,
    getRecipeLayerPercentage,
  } = useChargeColorsAndWeights();

  const {
    handleOpenHeatNameDialog,
    handleCloseHeatNameDialog,
    handleSaveHeatName,
    isHeatMutating,
  } = useHeatHandlers({
    charge,
    setHeatNameInput,
    setIsHeatNameDialogOpen,
    currentOperatorId,
    charges: allCharges,
    setError,
    setNotificationMessage,
    onHeatConflict: (newHeatName, conflictingChargeData) => {
      setConflictingCharge(conflictingChargeData);
      setHeatConflict({ isOpen: true, heatName: newHeatName });
    },
  });

  const {
    handleChargeSelect,
    handleAddCharge,
    handleNewChargeSchedule,
    handleNewChargeCancel,
    handleNewChargeStart,
  } = useChargeHandlers({
    currentOperatorId,
    charge,
    maxCharges: MAX_CHARGES,
    charges,
    refetchCharges,
    chargeNumbers,
    setCurrentChargeSequenceNumber,
    setCurrentChargeNumber,
    setIsNewChargeMode,
    setError,
    setNotificationMessage,
    setIsChangingCharge,
  });

  const {
    handleLayerSelect,
    handleAddNewLayer,
    handleCloseLayerModal,
    handleSaveLayer,
    handleDeleteLayer,
    isLayerMutating,
  } = useLayerHandlers({
    isEditMode,
    currentOperatorId,
    chargeLayers,
    selectedLayer,
    setSelectedLayer,
    charge,
    setIsLayerModalOpen,
    setError,
    setNotificationMessage,
  });

  const {
    handleRecipeSelect,
    handleOpenRecipeDialog,
    handleCloseRecipeDialog,
    handleConfirmRecipeModal,
  } = useRecipeHandlers({
    currentOperatorId,
    currentChargeNumber,
    setCurrentChargeNumber,
    charge,
    setShowRecipeConfirmModal,
    setSelectedRecipeId,
    setIsRecipeDialogOpen,
    setError,
    setNotificationMessage,
    refreshStaticData: refreshData,
  });

  const {
    handleBucketSelect,
    handleOpenBucketDialog,
    handleCloseBucketDialog,
    isBucketMutating,
  } = useBucketHandlers({
    currentOperatorId,
    currentChargeSequenceNumber,
    charge,
    setIsBucketDialogOpen,
    refreshData,
    setError,
    setNotificationMessage,
  });

  const {
    handleSaveComments,
    handleOpenCommentModal,
    handleCloseCommentModal,
    isCommentMutating,
  } = useCommentsHandlers({
    currentOperatorId,
    charge,
    setCommentInput,
    setKeyboardVisible,
    setIsCommentModalOpen,
    setError,
    setNotificationMessage,
  });

  const {
    handleToggleMode,
    handleBack,
    handleStatusAction,
    handleSaveManualWeight,
    handleChargeIntoEAF,
    isCustomMutating,
  } = useCustomHandlers({
    currentOperatorId,
    chargeLayers,
    isEditMode,
    setIsEditMode,
    setIsEditingManualWeight,
    charge,
    setSelectedLayer,
    setError,
    setNotificationMessage,
    allCharges,
    setErrorDialogData,
    setErrorDialogOpen,
  });

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setErrorDialogData(null);
    setConflictingCharge(null);
  };

  const handleContinueWithConflictingCharge = () => {
    if (!conflictingCharge) return;

    handleErrorDialogClose();
    navigate(
      `/scrap-charges/operation/${conflictingCharge.ChargeId}/continue/${
        conflictingCharge.ChargeGroupNumber
      }?operatorId=${currentOperatorId || ""}`,
      {
        state: {
          chargeNumber: conflictingCharge.ChargeNumber || 1,
        },
      },
    );
  };

  const handleContinueWithSelectedCharge = async () => {
    if (!conflictingCharge || !charge) return;

    try {
      // Update conflicting charge status to "H" (Charged Into EAF)
      await upsertChargeMutation.mutateAsync({
        ...conflictingCharge,
        ChargeStatusCode: "H",
        OperatorId: currentOperatorId,
      });

      // Open the selected charge
      await openChargeMutation.mutateAsync(charge.ChargeId as number);

      handleErrorDialogClose();
      setNotificationMessage("Charge is now Open");
    } catch (err) {
      console.error("Error handling charge conflict:", err);
      setError(
        "Error handling charge conflict: " +
          (err instanceof Error ? err.message : String(err)),
      );
    }
  };

  const handleOpenCommodityModal = (layer: ChargeLayer) => {
    setSelectedLayer(layer);

    // Look up the recipe's original commodity for this layer
    let recipeCommodity: typeof commoditySwitchState.originalRecipeCommodity =
      null;
    if (layer.RecipeLayerId) {
      const recipeLayer = recipeLayers.find(
        (rl) => rl.RecipeLayerId === layer.RecipeLayerId,
      );
      if (
        recipeLayer?.CommodityId &&
        recipeLayer.CommodityId !== layer.CommodityId
      ) {
        recipeCommodity = {
          commodityId: recipeLayer.CommodityId,
          commodityName: recipeLayer.CommodityName || "Unknown",
          commodityColor: recipeLayer.CommodityColor || null,
        };
      }
    }

    setCommoditySwitchState({
      isOpen: true,
      originalCommodityId: layer.CommodityId || null,
      selectedCommodityId: layer.CommodityId || null,
      originalRecipeCommodity: recipeCommodity,
    });
  };

  const handleCommoditySelect = async (commodityId: number) => {
    if (selectedLayer) {
      await handleSaveLayer({
        commodityId,
        weight: selectedLayer.Weight || 0,
        layerNumber: selectedLayer.LayerNumber,
        setAsActive: selectedLayer.IsCurrent || false,
      });
    }
    handleCloseCommodityModal();
  };

  const handleCloseCommodityModal = () => {
    setCommoditySwitchState({
      isOpen: false,
      originalCommodityId: null,
      selectedCommodityId: null,
      originalRecipeCommodity: null,
    });
  };

  // When the user switches to a different charge, reset UI state.
  // Charge layers and recipe layers are loaded automatically by TanStack Query hooks.
  useEffect(() => {
    if (charge?.ChargeId) {
      const selectedBucket = buckets.find(
        (b) => b.BucketId === charge.BucketId,
      );
      if (selectedBucket) {
        const capacity = selectedBucket.Capacity || 0;
        setBucketCapacity(capacity);
        setUiCapacity(capacity);
      }

      // Sync selectedRecipeId for the recipe modal
      setSelectedRecipeId(charge.RecipeId || null);

      // Set ChargeSequenceNumber if it exists, otherwise set to null
      setCurrentChargeSequenceNumber(charge.ChargeSequenceNumber || null);

      // Set ChargeNumber
      setCurrentChargeNumber(charge.ChargeNumber || null);

      // Clear the changing charge flag after all data is loaded
      setIsChangingCharge(false);
    } else {
      // No charge - reset UI state
      setSelectedRecipeId(null);
      setCurrentChargeNumber(null);
      setCurrentChargeSequenceNumber(null);
    }
    setCommentInput(charge?.Comments || "");
  }, [charge?.ChargeId]);

  // Charge-not-found detection: when TanStack Query has loaded but the chargeId doesn't match any charge
  useEffect(() => {
    if (!isChargesLoading && chargeId && allCharges.length > 0 && !charge) {
      setErrorDialogData({
        title: "Charge Not Found",
        message: `The requested charge with ID "${chargeId}" could not be found.`,
        guidanceMessage:
          "The charge may have been deleted or the ID is invalid. Please return to the selection screen.",
      });
      setErrorDialogOpen(true);
    }
  }, [isChargesLoading, chargeId, allCharges.length, charge]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const opId = queryParams.get("operatorId");

    if (opId) {
      const parsedOpId = parseInt(opId);
      if (!isNaN(parsedOpId)) {
        setCurrentOperatorId(parsedOpId);
        return;
      }
    }

    // If no operatorId in URL, try to get from localStorage
    const storedOperatorId = localStorage.getItem("selectedOperatorId");
    if (storedOperatorId) {
      const parsedStoredOpId = parseInt(storedOperatorId);
      if (!isNaN(parsedStoredOpId)) {
        setCurrentOperatorId(parsedStoredOpId);
        // Update URL to reflect the stored operator
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set("operatorId", parsedStoredOpId.toString());
        window.history.replaceState(
          {},
          "",
          `${location.pathname}?${newSearchParams.toString()}`,
        );
      }
    }
  }, [chargeId, location.search]);

  useEffect(() => {
    if (isEditMode && charge?.ChargeStatusCode !== "O") {
      setIsEditMode(false);
      setSelectedLayer(null);
    }
  }, [charge?.ChargeId, charge?.ChargeStatusCode]);

  return (
    <>
      <HelmetDexteel
        title={`${
          charge?.ScheduledHeatName ? `${charge.ScheduledHeatName} - ` : ""
        }Charge Operation`}
      />
      <Box sx={styles.root}>
        <Box sx={styles.header}>
          <Box
            sx={{
              width: "33%",
              padding: "0 16px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Button
              component={Link}
              to="/meltshop-dashboard"
              sx={{
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "uppercase",
              }}
            >
              Dashboard
            </Button>
          </Box>
          <Box
            sx={{
              width: "34%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h4" sx={styles.headerTitle}>
              Charge Operation
            </Typography>
          </Box>
          <Box
            sx={{
              width: "33%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "24px",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedOperator?.OperatorName || "No Operator"}
            </Typography>
            <TimeAndUserMenu />
          </Box>
        </Box>

        <Box sx={styles.mainContainer}>
          {charge ? (
            <>
              {/* Sidebar */}
              <Box sx={styles.sidebar}>
                {/* Scheduled Heat, Recipe and Comments */}
                <Box sx={styles.sidebarSection}>
                  <Box display="flex" alignItems="flex-end" sx={{ gap: 1.25 }}>
                    <Box flex={1} display="flex" flexDirection="column">
                      <Typography sx={styles.infoLabel}>
                        Scheduled Heat
                      </Typography>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => handleOpenHeatNameDialog()}
                        sx={{
                          height: "40px",
                          fontSize: "0.9rem",
                          fontWeight: "bold",
                        }}
                        disabled={charge.ChargeStatusCode === "H"}
                      >
                        {getScheduledHeatName(charge)}
                      </Button>
                    </Box>

                    <Box flex={1} display="flex" flexDirection="column">
                      <Typography sx={styles.infoLabel}>Recipe</Typography>
                      <Button
                        fullWidth
                        variant="contained"
                        color={isRecipeEnabled(charge) ? "primary" : "inherit"}
                        onClick={() => handleOpenRecipeDialog()}
                        sx={{
                          height: "40px",
                          fontSize: "0.9rem",
                          fontWeight: "bold",
                          lineHeight: 1.1,
                          ...(isRecipeEnabled(charge)
                            ? null
                            : {
                                backgroundColor: "grey.400",
                                "&:hover": {
                                  backgroundColor: "grey.500",
                                },
                              }),
                        }}
                        disabled={
                          charge.ChargeStatusCode === "H" ||
                          (charge.ChargeNumber ?? 0) > 3
                        }
                      >
                        {getSelectedRecipeName(charge)}
                      </Button>
                    </Box>
                    <Tooltip
                      title={
                        charge?.Comments
                          ? `"${charge?.Comments}"`
                          : "Add Comment"
                      }
                    >
                      <Badge
                        badgeContent={charge?.Comments ? " " : 0}
                        sx={
                          charge?.Comments
                            ? styles.smallBadge
                            : styles.hiddenBadge
                        }
                      >
                        <IconButton
                          onClick={handleOpenCommentModal}
                          color="primary"
                          sx={{
                            padding: "4px",
                            height: "40px",
                            width: "40px",
                          }}
                        >
                          <Comment sx={{ fontSize: "2rem" }} color="primary" />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Charges */}
                <Box sx={styles.chargesScrollContainer}>
                  <Box sx={styles.sidebarSection}>
                    <Typography sx={styles.infoLabel}>Charges</Typography>
                    {chargeNumbers.map((chargeNum) => {
                      const isActive = charge.ChargeNumber === chargeNum;

                      const chargeData =
                        charge.ChargeGroupNumber === null
                          ? charge.ChargeNumber === chargeNum
                            ? charge
                            : null
                          : charges.find(
                              (c: Charge) =>
                                c.ChargeGroupNumber ===
                                  charge.ChargeGroupNumber &&
                                c.ChargeNumber === chargeNum,
                            );

                      const hasCharge = !!chargeData;
                      const chargeBucket = chargeData?.BucketId
                        ? buckets.find(
                            (b) => b.BucketId === chargeData.BucketId,
                          )?.BucketName || "N/A"
                        : "N/A";
                      const chargeStatus = chargeData?.ChargeStatusCode || "";

                      return (
                        <Box
                          key={chargeNum}
                          display="flex"
                          flexDirection="column"
                          sx={{ gap: 1 }}
                        >
                          <Button
                            fullWidth
                            variant={isActive ? "contained" : "outlined"}
                            color="primary"
                            onClick={() => handleChargeSelect(chargeNum)}
                            sx={{
                              height: "48px",
                              fontSize: "1.1rem",
                              fontWeight: isActive ? 600 : 500,
                              border: isActive
                                ? `3px solid ${theme.palette.primary.dark}`
                                : undefined,
                              boxShadow: isActive
                                ? theme.shadows[3]
                                : undefined,
                              "&:hover": isActive
                                ? {
                                    border: `3px solid ${theme.palette.primary.dark}`,
                                  }
                                : undefined,
                            }}
                            disabled={!hasCharge}
                          >
                            Charge {chargeNum}
                          </Button>
                          {hasCharge ? (
                            <Box display="flex" sx={{ gap: 1 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  if (isActive) handleOpenBucketDialog();
                                }}
                                sx={{
                                  flex: 1,
                                  height: "40px",
                                  fontSize: "0.9rem",
                                  fontWeight: "bold",
                                }}
                                disabled={!isActive || chargeStatus === "H"}
                              >
                                {chargeBucket}
                              </Button>
                              <Box
                                sx={{
                                  ...(styles.statusChip as object),
                                  ...(chargeStatus === "C"
                                    ? (styles.statusClosed as object)
                                    : chargeStatus === "O"
                                      ? (styles.statusOpen as object)
                                      : chargeStatus === "H"
                                        ? (styles.statusEAF as object)
                                        : {}),
                                  fontSize: "0.85rem",
                                  padding: "4px 8px",
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "100px",
                                }}
                              >
                                {getStatusName(chargeStatus).toUpperCase()}
                              </Box>
                            </Box>
                          ) : null}
                          <Divider sx={{ my: 1 }} />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* New Charge Button */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAddCharge}
                  sx={{ ...(styles.newChargeButton as object), mt: 1 }}
                  disabled={!charges || charges.length === 0}
                >
                  <Box sx={styles.buttonWithIcon}>
                    <Add />
                    NEW CHARGE
                  </Box>
                </Button>
              </Box>

              {/* Main Content */}
              <Paper
                elevation={3}
                sx={{ ...(styles.paper as object), position: "relative" }}
              >
                {isChangingCharge ? (
                  <Box
                    position="absolute"
                    width="100%"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(255, 255, 255, 0.8)"
                    zIndex={1000}
                    sx={{ borderRadius: "inherit" }}
                  >
                    <CircularProgress size={60} />
                  </Box>
                ) : null}
                <Box sx={styles.contentSection}>
                  <Box sx={styles.leftPanel}>
                    <Box sx={styles.panelHeader}>
                      <Typography sx={styles.panelTitle}>
                        Current Layers
                      </Typography>
                      <Typography sx={styles.weightTextNormal}>
                        <Box component="span" sx={styles.weightLabel}>
                          Scrap Weight:
                        </Box>{" "}
                        {formatCraneMagnitude(scrapWeight, "0")}
                      </Typography>
                      <Typography sx={styles.weightTextNormal}>
                        <Box component="span" sx={styles.weightLabel}>
                          Total Weight:
                        </Box>{" "}
                        {formatCraneMagnitude(totalWeight, "0")}
                      </Typography>
                    </Box>
                    <Box sx={styles.panelContent}>
                      <TableCurrentLayers
                        recipeLayers={recipeLayers}
                        alignedRows={currentLayerRows}
                        requestedWeightSuggestions={
                          requestedWeightLayerSuggestions
                        }
                        commodities={commodities}
                        selectedLayer={selectedLayer}
                        getCommodityColor={getCommodityColor}
                        getBgColorWithAlpha={getBgColorWithAlpha}
                        handleLayerSelect={handleLayerSelect}
                        onOpenCommodityModal={handleOpenCommodityModal}
                        longPressDuration={onHoldTime}
                      />
                    </Box>

                    {isEditMode && !selectedRecipeId ? (
                      <Box sx={styles.chargeLayerFooter}>
                        <Box sx={styles.layerActionButtons}>
                          <Button
                            variant="contained"
                            sx={styles.editWeightButton}
                            onClick={(event) => {
                              event.currentTarget.blur();
                              setIsEditingManualWeight(true);
                            }}
                            startIcon={<Edit />}
                          >
                            Set Target Weight
                          </Button>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>

                  <Box sx={styles.rightPanel}>
                    <Box sx={styles.panelHeader}>
                      <Typography sx={styles.panelTitle}>
                        Recipe Layers
                      </Typography>
                      {hasScaledTarget ? (
                        <Typography
                          sx={{
                            ...(styles.targetWeight as object),
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box component="span" sx={styles.weightLabel}>
                            Total Weight:
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              opacity: 0.75,
                              textDecoration: "line-through",
                            }}
                          >
                            {formatCraneMagnitude(recipeTargetWeight, "0")}
                          </Box>
                          <Box component="span">
                            {formatCraneMagnitude(
                              scaledChargeTarget.scaledTotalTargetWeight,
                              "0",
                            )}
                          </Box>
                        </Typography>
                      ) : (
                        <Typography sx={styles.targetWeight}>
                          <Box component="span" sx={styles.weightLabel}>
                            Target Weight:
                          </Box>{" "}
                          {formatCraneMagnitude(recipeTargetWeight, "0")}
                        </Typography>
                      )}
                    </Box>
                    {selectedRecipeId ? (
                      <>
                        <Box sx={styles.panelContent}>
                          <TableRecipeLayers
                            commodities={commodities}
                            alignedRows={recipeLayerRows}
                            recipeTargetWeight={recipeTargetWeight}
                            suggestedLayerWeights={
                              requestedWeightLayerSuggestions?.targetWeightsByRecipeLayerId
                            }
                            getCommodityColor={getCommodityColor}
                            getBgColorWithAlpha={getBgColorWithAlpha}
                            getRecipeLayerPercentage={getRecipeLayerPercentage}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box
                        sx={{
                          ...(styles.panelContent as object),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="body1">
                          No recipe selected
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box sx={styles.actionButtons}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBack}
                    sx={{ ...(styles.touchButton as object), width: "32%" }}
                  >
                    <Box sx={styles.buttonWithIcon}>
                      <ArrowBack />
                      BACK TO SELECTION
                    </Box>
                  </Button>

                  {isEditMode ? (
                    <Button
                      variant="contained"
                      onClick={handleAddNewLayer}
                      disabled={isLayerMutating}
                      sx={{
                        ...(styles.addLayerBottomButton as object),
                        width: "8%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1.05,
                        }}
                      >
                        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                          + ADD
                        </Box>
                        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                          LAYER
                        </Box>
                      </Box>
                    </Button>
                  ) : null}

                  <Button
                    variant="contained"
                    onClick={handleToggleMode}
                    sx={{
                      ...(styles.touchButton as object),
                      ...(isEditMode
                        ? (styles.runModeButton as object)
                        : (styles.editModeButton as object)),
                      width: isEditMode
                        ? charge.ChargeStatusCode === "H"
                          ? "22%"
                          : "22.67%"
                        : "32%",
                    }}
                    disabled={charge.ChargeStatusCode !== "O"}
                  >
                    <Box sx={styles.buttonWithIcon}>
                      {isEditMode ? <KeyboardReturn /> : <Edit />}
                      {isEditMode ? "BACK TO RUN MODE" : "GO TO EDIT MODE"}
                    </Box>
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleStatusAction}
                    color={
                      charge.ChargeStatusCode === "S" ||
                      charge.ChargeStatusCode === "O"
                        ? "primary"
                        : undefined
                    }
                    sx={{
                      ...(styles.touchButton as object),
                      ...(charge.ChargeStatusCode === "O"
                        ? (styles.closeButton as object)
                        : charge.ChargeStatusCode === "C" ||
                            charge.ChargeStatusCode === "S"
                          ? (styles.openButton as object)
                          : {}),
                      width: charge.ChargeStatusCode !== "H" ? "20%" : "32%",
                    }}
                    disabled={charge.ChargeStatusCode === "H"}
                  >
                    <Box sx={styles.buttonWithIcon}>
                      {charge.ChargeStatusCode === "S" && <PlayArrow />}
                      {charge.ChargeStatusCode === "C" && <Replay />}
                      {charge.ChargeStatusCode === "O" && <Close />}
                      {charge.ChargeStatusCode === "S"
                        ? "START"
                        : charge.ChargeStatusCode === "O"
                          ? "CLOSE CHARGE"
                          : charge.ChargeStatusCode === "C"
                            ? "REOPEN"
                            : "CHARGED INTO EAF"}
                    </Box>
                  </Button>

                  {charge.ChargeStatusCode !== "H" ? (
                    <Button
                      variant="contained"
                      sx={{
                        ...(styles.touchButton as object),
                        width: "12%",
                        backgroundColor: (theme) => theme.palette.grey[700],
                        color: "white",
                      }}
                      onClick={() => setIsEAFConfirmModalOpen(true)}
                    >
                      <Box sx={styles.buttonWithIcon}>
                        EAF | <Block sx={{ fontSize: "2.5rem" }} />
                      </Box>
                    </Button>
                  ) : null}
                </Box>
              </Paper>
            </>
          ) : null}
        </Box>

        <ConfirmRecipeModal
          selectedRecipeId={selectedRecipeId}
          showRecipeConfirmModal={showRecipeConfirmModal}
          handleConfirmRecipeModal={handleConfirmRecipeModal}
        />

        <RecipeModal
          recipes={recipes}
          totalWeight={totalWeight}
          chargeLayers={chargeLayers}
          isRecipeDialogOpen={isRecipeDialogOpen}
          setIsRecipeDialogOpen={setIsRecipeDialogOpen}
          selectedRecipeId={selectedRecipeId}
          setSelectedRecipeId={setSelectedRecipeId}
          setShowRecipeConfirmModal={setShowRecipeConfirmModal}
          handleRecipeSelect={handleRecipeSelect}
          handleCloseRecipeDialog={handleCloseRecipeDialog}
        />

        <CommodityModal
          commodities={availableCommoditiesForSwitch}
          isCommodityDialogOpen={commoditySwitchState.isOpen}
          onClose={handleCloseCommodityModal}
          selectedCommodityId={commoditySwitchState.selectedCommodityId}
          setSelectedCommodityId={(id) =>
            setCommoditySwitchState((prev) => ({
              ...prev,
              selectedCommodityId: id,
            }))
          }
          handleCommoditySelect={handleCommoditySelect}
          hasSubstitutes={hasSubstitutes}
          originalRecipeCommodity={commoditySwitchState.originalRecipeCommodity}
        />

        <BucketModal
          buckets={buckets}
          selectedBucketId={charge?.BucketId || null}
          isBucketDialogOpen={isBucketDialogOpen}
          handleCloseBucketDialog={handleCloseBucketDialog}
          handleBucketSelect={handleBucketSelect}
          charges={charges}
          allCharges={allCharges}
          currentChargeId={charge?.ChargeId || null}
        />

        <CommentsModal
          isLoading={isCommentMutating}
          keyboardVisible={keyboardVisible}
          setKeyboardVisible={setKeyboardVisible}
          commentInput={commentInput}
          setCommentInput={setCommentInput}
          isCommentModalOpen={isCommentModalOpen}
          handleCloseCommentModal={handleCloseCommentModal}
          handleSaveComments={handleSaveComments}
        />

        <HeatNameModal
          isLoading={isHeatMutating}
          isHeatNameDialogOpen={isHeatNameDialogOpen}
          heatNameInput={heatNameInput}
          setHeatNameInput={setHeatNameInput}
          handleCloseHeatNameDialog={handleCloseHeatNameDialog}
          handleSaveHeatName={handleSaveHeatName}
        />

        <RequestedHeatWeightModal
          open={isEditingManualWeight}
          requestedHeatWeight={charge?.ManualTargetWeight ?? null}
          recipeTotalWeight={0}
          scheduledHeatName={null}
          isSaving={isCustomMutating}
          title="Target Weight"
          inputLabel="Target Weight"
          onCancel={() => setIsEditingManualWeight(false)}
          onSave={handleSaveManualWeight}
        />

        <ErrorDetailsModal
          open={heatConflict.isOpen}
          title="Heat Name Conflict Error"
          message={`Cannot assign heat name "${heatConflict.heatName}" because it already has a charge with Charge Number #${conflictingCharge?.ChargeNumber}.`}
          details={[
            { label: "Charge ID", value: conflictingCharge?.ChargeId },
            { label: "Charge Number", value: conflictingCharge?.ChargeNumber },
            {
              label: "Status",
              value: getStatusName(conflictingCharge?.ChargeStatusCode || ""),
            },
          ]}
          detailsTitle="Conflicting Charge Details:"
          guidanceMessage="Each heat can only have one charge with the same charge number. Please choose a different heat name."
          onClose={() => setHeatConflict({ isOpen: false, heatName: "" })}
        />

        <WarningModal
          open={isEAFConfirmModalOpen}
          title="Charge Into EAF or Not Used"
          message="The charge will be permanently marked as charged into EAF (or Not Used) and removed from the active queue. This cannot be undone."
          guidanceMessage="Are you sure you want to proceed?"
          onCancel={() => setIsEAFConfirmModalOpen(false)}
          onProceed={() => {
            handleChargeIntoEAF();
            setIsEAFConfirmModalOpen(false);
          }}
          cancelText="CANCEL"
          proceedText="CONFIRM"
          proceedColor="secondary"
        />

        <ChargeLayerModalForOperation
          open={isLayerModalOpen}
          onClose={handleCloseLayerModal}
          onSave={handleSaveLayer}
          onDelete={selectedLayer ? handleDeleteLayer : undefined}
          isLoading={isLayerMutating}
          title={selectedLayer ? "Edit Layer" : "Add Layer"}
          chargeId={charge?.ChargeId as number}
          selectedLayer={selectedLayer}
          maxLayerNumber={maxLayerNumber}
        />

        <Dialog
          open={isNewChargeMode}
          onClose={handleNewChargeCancel}
          maxWidth="sm"
          fullWidth
        >
          <Box p={4}>
            <NewChargePanel
              open={isNewChargeMode}
              onSchedule={handleNewChargeSchedule}
              onCancel={handleNewChargeCancel}
              onStart={handleNewChargeStart}
              operatorId={currentOperatorId}
              chargeGroupNumber={charge?.ChargeGroupNumber}
              scheduledHeatName={charge?.ScheduledHeatName}
              presetChargeNumber={chargeNumbers.length + 1}
              disableHeatNameEdit={true}
              charges={allCharges}
            />
          </Box>
        </Dialog>

        <NotificationSnackBar
          message={notificationMessage}
          onHide={() => setNotificationMessage("")}
        />

        {errorDialogData ? (
          <ErrorDetailsModal
            open={errorDialogOpen}
            title={errorDialogData.title}
            message={errorDialogData.message}
            details={errorDialogData.details}
            detailsTitle={errorDialogData.detailsTitle}
            guidanceMessage={errorDialogData.guidanceMessage}
            onClose={() => {
              const isChargeNotFound =
                errorDialogData?.title === "Charge Not Found";
              handleErrorDialogClose();
              // If charge not found, redirect to selection
              if (isChargeNotFound) {
                handleBack();
              }
            }}
            isBucketConflict={errorDialogData.isBucketConflict}
            conflictingCharge={conflictingCharge}
            selectedCharge={charge}
            onContinueWithConflictingCharge={
              handleContinueWithConflictingCharge
            }
            onContinueWithSelectedCharge={handleContinueWithSelectedCharge}
          />
        ) : null}
      </Box>
    </>
  );
};
